import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

// API Base URLs
const VITE_API_URL = import.meta.env.VITE_API_URL;
const DEFAULT_API_URL = 'http://localhost:8000/api/v1';

// Determine the API base URL with proper fallback
let API_BASE_URL = DEFAULT_API_URL;

if (VITE_API_URL) {
  if (VITE_API_URL.startsWith('http://') || VITE_API_URL.startsWith('https://')) {
    API_BASE_URL = VITE_API_URL;
  } else if (VITE_API_URL.includes('://')) {
    API_BASE_URL = DEFAULT_API_URL;
  } else {
    API_BASE_URL = 'https://' + VITE_API_URL;
  }
} else {
  API_BASE_URL = DEFAULT_API_URL;
}

// In production, enforce HTTPS
if (import.meta.env.PROD && API_BASE_URL.startsWith('http://') && !API_BASE_URL.includes('localhost')) {
  API_BASE_URL = API_BASE_URL.replace('http://', 'https://');
}

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = cookies.get('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.log("Token rejecting", error);
    return Promise.reject(error);
  }
);

// Token refresh state
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = cookies.get('refreshToken');

      if (!refreshToken) {
        // No refresh token available, redirect to login
        isRefreshing = false;
        cookies.remove('token', { path: '/' });
        cookies.remove('refreshToken', { path: '/' });
        cookies.remove('userData', { path: '/' });
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Try to refresh the token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token: new_refresh_token } = response.data;

        // Update cookies with new tokens
        const cookieOptions = {
          path: '/',
          secure: import.meta.env.PROD,
          sameSite: 'lax' as const,
          maxAge: 7 * 24 * 60 * 60, // 7 days
        };

        cookies.set('token', access_token, cookieOptions);
        cookies.set('refreshToken', new_refresh_token, cookieOptions);

        // Update authorization header
        apiClient.defaults.headers.common.Authorization = `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        // Process queued requests
        processQueue(null, access_token);

        isRefreshing = false;

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear everything and redirect to login
        processQueue(refreshError, null);
        isRefreshing = false;

        cookies.remove('token', { path: '/' });
        cookies.remove('refreshToken', { path: '/' });
        cookies.remove('userData', { path: '/' });

        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden - check if user is inactive
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail;
      // Log out if user is inactive or account is disabled
      if (detail === 'Inactive user' || detail === 'User account is inactive') {
        cookies.remove('token', { path: '/' });
        cookies.remove('refreshToken', { path: '/' });
        cookies.remove('userData', { path: '/' });
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// TYPES
// ============================================================================

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  skip: number;
  limit: number;
  page: number;
  total_pages: number;
}

export interface PaginationParams {
  skip?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

// ============================================================================
// AUTHENTICATION
// ============================================================================

export interface LoginRequest {
  email: string;
  password: string;
  recaptcha_token?: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  role: string;
  profile_image_url?: string | null;
}

export interface RefreshTokenResponse {
  access_token: string;
  token_type: string;
}

export const authApi = {
  login: (data: LoginRequest) =>
    apiClient.post<LoginResponse>('/auth/login', data),

  refreshToken: (refreshToken: string) =>
    apiClient.post<RefreshTokenResponse>('/auth/refresh', { refresh_token: refreshToken }),

  changePassword: (oldPassword: string, newPassword: string) =>
    apiClient.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),

  forgotPassword: (phoneNumber: string, recaptchaToken?: string) =>
    apiClient.post('/auth/forgot-password', { phone_number: phoneNumber, recaptcha_token: recaptchaToken }),

  resetPassword: (phoneNumber: string, resetCode: string, newPassword: string) =>
    apiClient.post('/auth/reset-password', {
      phone_number: phoneNumber,
      reset_code: resetCode,
      new_password: newPassword
    }),

  uploadProfilePicture: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/auth/upload-profile-picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteProfilePicture: () =>
    apiClient.delete('/auth/delete-profile-picture'),

  requestAccountDeletion: (email: string, phoneNumber: string, reason: string) =>
    apiClient.post('/auth/request-delete', { email, phone_number: phoneNumber, reason }),

  getRecaptchaSiteKey: () =>
    apiClient.get<{ site_key: string }>('/auth/recaptcha-site-key'),
};

// ============================================================================
// USERS
// ============================================================================

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;  // Optional for admin users
  ssn?: string;  // Optional for admin users
  birth_date: string;
  gender: string;
  city_province_id?: number;
  district_town_id?: number;
  role: 'archer' | 'referee' | 'leader' | 'admin' | 'superadmin';
  is_handicapped: boolean;
  is_old: boolean;
  affiliation?: string;
  profile_image_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface CreateUserRequest {
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;  // Optional for admin users
  ssn?: string;  // Optional for admin users
  birth_date: string;
  gender: string;
  password: string;
  role?: string;
  city_province_id?: number;
  district_town_id?: number;
  is_handicapped?: boolean;
  is_old?: boolean;
  affiliation?: string;
}

export interface UpdateUserRequest extends Partial<CreateUserRequest> {}

export interface GetUsersParams extends PaginationParams {
  role?: string;
  gender?: string;
  is_active?: boolean;
  search?: string;
}

export interface UserSSNResponse {
  user_id: number;
  ssn: string;
}

export interface UserBySSNResponse {
  found: boolean;
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    birth_date: string | null;
    gender: string;
    is_handicapped: boolean;
    is_old: boolean;
    affiliation?: string;
    role: string;
  } | null;
  is_member: boolean;
  user_club: {
    id: number;
    club_id: string;
    user_id: number;
    rank_id: number | null;
    rank_date: string | null;
    rate_id: number | null;
    rate_date: string | null;
    team_id: number | null;
    is_team_leader: boolean;
  } | null;
}

export const usersApi = {
  getAll: (params?: GetUsersParams) =>
    apiClient.get<PaginatedResponse<User>>('/users/', { params }),

  getById: (id: number) =>
    apiClient.get<User>(`/users/${id}/`),

  getSSN: (id: number) =>
    apiClient.get<UserSSNResponse>(`/users/${id}/ssn`),

  getBySSN: (ssn: string, clubId: string) =>
    apiClient.get<UserBySSNResponse>(`/users/by-ssn/${ssn}`, { params: { club_id: clubId } }),

  create: (data: CreateUserRequest) =>
    apiClient.post<User>('/users/', data),

  update: (id: number, data: UpdateUserRequest) =>
    apiClient.put<User>(`/users/${id}/`, data),

  delete: (id: number) =>
    apiClient.delete(`/users/${id}/`),

  resetPassword: (id: number, newPassword: string) =>
    apiClient.put(`/users/${id}/reset-password`, { new_password: newPassword }),
};

// ============================================================================
// TEAMS
// ============================================================================

export interface Team {
  id: number;
  club_id: string;
  name: string;
  image_url?: string;
  information?: string;
  leader_id?: number;
  member_ids?: number[];
  created_at: string;
  updated_at?: string;
}

export interface CreateTeamRequest {
  club_id: string;
  name: string;
  image_url?: string;
  information?: string;
  leader_id?: number;
  member_ids?: number[];
}

export interface UpdateTeamRequest extends Partial<CreateTeamRequest> {}

export interface GetTeamsParams extends PaginationParams {
  club_id?: string;
  leader_id?: number;
  search?: string;
}

export const teamsApi = {
  getAll: (params?: GetTeamsParams) =>
    apiClient.get<PaginatedResponse<Team>>('/teams', { params }),

  getById: (id: number) =>
    apiClient.get<Team>(`/teams/${id}`),

  create: (data: CreateTeamRequest) =>
    apiClient.post<Team>('/teams', data),

  update: (id: number, data: UpdateTeamRequest) =>
    apiClient.put<Team>(`/teams/${id}`, data),

  delete: (id: number) =>
    apiClient.delete(`/teams/${id}`),
};

// ============================================================================
// RANKS
// ============================================================================

export interface Rank {
  id: number;
  club_id: string;
  name: string;
  is_rank: boolean;
  max_point: number;
  min_point: number;
  order: number;
  created_at: string;
  updated_at?: string;
}

export interface CreateRankRequest {
  club_id: string;
  name: string;
  is_rank: boolean;
  max_point?: number;
  min_point?: number;
  order: number;
}

export interface UpdateRankRequest extends Partial<CreateRankRequest> {}

export interface GetRanksParams extends PaginationParams {
  club_id?: string;
  is_rank?: boolean;
  search?: string;
}

export interface RatingUser {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  ssn: string;
  rank_id?: number;
  rank_name?: string;
  rank_order?: number;
  rank_date?: string;
  rate_id?: number;
  rate_date?: string;
  rate_point?: number;
  team_id?: number;
  team_name?: string;
  total_chansaa: number;
}

export interface GetRatingParams extends PaginationParams {
  club_id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  rank_name?: string;
}

export const ranksApi = {
  getAll: (params?: GetRanksParams) =>
    apiClient.get<PaginatedResponse<Rank>>('/ranks', { params }),

  getRating: (params: GetRatingParams) =>
    apiClient.get<PaginatedResponse<RatingUser>>('/ranks/rating', { params }),

  getById: (id: number) =>
    apiClient.get<Rank>(`/ranks/${id}`),

  create: (data: CreateRankRequest) =>
    apiClient.post<Rank>('/ranks', data),

  update: (id: number, data: UpdateRankRequest) =>
    apiClient.put<Rank>(`/ranks/${id}`, data),

  delete: (id: number) =>
    apiClient.delete(`/ranks/${id}`),
};

// ============================================================================
// NEWS
// ============================================================================

export interface News {
  id: number;
  author_id: number;
  author_name: string;
  author_image_url?: string;
  club_id: string;
  title: string;
  description?: string;
  content: string;
  image_name?: string;
  image_url?: string;
  created_at: string;
  updated_at?: string;
}

export interface CreateNewsRequest {
  author_id: number;
  author_name: string;
  author_image_url?: string;
  club_id: string;
  title: string;
  description?: string;
  content: string;
  image_name?: string;
  image_url?: string;
}

export interface UpdateNewsRequest extends Partial<CreateNewsRequest> {}

export interface GetNewsParams extends PaginationParams {
  club_id?: string;
  author_id?: number;
  search?: string;
}

export const newsApi = {
  getAll: (params?: GetNewsParams) =>
    apiClient.get<PaginatedResponse<News>>('/news', { params }),

  getById: (id: number) =>
    apiClient.get<News>(`/news/${id}`),

  create: (data: CreateNewsRequest) =>
    apiClient.post<News>('/news', data),

  update: (id: number, data: UpdateNewsRequest) =>
    apiClient.put<News>(`/news/${id}`, data),

  delete: (id: number) =>
    apiClient.delete(`/news/${id}`),

  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<{ image_url: string; image_name: string }>('/news/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ============================================================================
// COMPETITIONS
// ============================================================================

export interface Competition {
  id: number;
  club_id: string;
  name: string;
  location: string;
  lat?: number;
  lng?: number;
  start_date: string;
  end_date: string;
  end_register_date: string;
  detail_link?: string;
  fee: number;
  guidance?: string;
  safety?: string;
  is_rank: boolean;
  referee_id?: number;
  referees_ids?: number[];
  status: string;
  zurhai: number;
  onog_ue: number;
  created_at: string;
  updated_at?: string;
}

export interface CreateCompetitionRequest {
  club_id: string;
  name: string;
  location: string;
  lat?: number;
  lng?: number;
  start_date: string;
  end_date: string;
  end_register_date: string;
  detail_link?: string;
  fee?: number;
  guidance?: string;
  safety?: string;
  is_rank?: boolean;
  referee_id?: number;
  referees_ids?: number[];
  status?: string;
  zurhai?: number;
  onog_ue?: number;
}

export interface UpdateCompetitionRequest extends Partial<CreateCompetitionRequest> {}

export interface GetCompetitionsParams extends PaginationParams {
  club_id?: string;
  status?: string;
  is_rank?: boolean;
  referee_id?: number;
  start_date_from?: string;
  start_date_to?: string;
  search?: string;
}

export interface CompetitorUserClub {
  rank_id?: number;
  rank_date?: string;
  rate_id?: number;
  rate_date?: string;
  rate_point?: number;
  team_id?: number;
  is_team_leader: boolean;
  rank?: {
    id: number;
    name: string;
    order: number;
  };
  team?: {
    id: number;
    name: string;
  };
}

export interface CompetitionCompetitor {
  id: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  ssn?: string;
  birth_date?: string;
  gender?: string;
  role?: string;
  profile_image_url?: string;
  is_handicapped: boolean;
  is_old: boolean;
  city_province_id?: number;
  district_town_id?: number;
  user_club?: CompetitorUserClub;
  registered_at?: string;
  invoice_id?: number;
}

export const competitionsApi = {
  getAll: (params?: GetCompetitionsParams) =>
    apiClient.get<PaginatedResponse<Competition>>('/competitions', { params }),

  getById: (id: number) =>
    apiClient.get<Competition>(`/competitions/${id}`),

  getCompetitors: (id: number, params?: { gender?: 'male' | 'female' }) =>
    apiClient.get<CompetitionCompetitor[]>(`/competitions/${id}/competitors`, { params }),

  create: (data: CreateCompetitionRequest) =>
    apiClient.post<Competition>('/competitions', data),

  update: (id: number, data: UpdateCompetitionRequest) =>
    apiClient.put<Competition>(`/competitions/${id}`, data),

  delete: (id: number) =>
    apiClient.delete(`/competitions/${id}`),
};

// ============================================================================
// REFEREES
// ============================================================================

export interface Referee {
  id: number;
  club_id: string;
  type: 'master' | 'panel';
  first_name: string;
  last_name: string;
  ssn: string;
  email: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface CreateRefereeRequest {
  club_id: string;
  type: 'master' | 'panel';
  first_name: string;
  last_name: string;
  ssn: string;
  email: string;
  password?: string;
}

export interface UpdateRefereeRequest extends Partial<CreateRefereeRequest> {}

export interface GetRefereesParams extends PaginationParams {
  club_id?: string;
  type?: 'master' | 'panel';
  search?: string;
}

export const refereesApi = {
  getAll: (params?: GetRefereesParams) =>
    apiClient.get<PaginatedResponse<Referee>>('/referees', { params }),

  getById: (id: number) =>
    apiClient.get<Referee>(`/referees/${id}`),

  create: (data: CreateRefereeRequest) =>
    apiClient.post<Referee>('/referees', data),

  update: (id: number, data: UpdateRefereeRequest) =>
    apiClient.put<Referee>(`/referees/${id}`, data),

  delete: (id: number) =>
    apiClient.delete(`/referees/${id}`),
};

// ============================================================================
// USER CLUBS (Club Memberships)
// ============================================================================

export interface UserClub {
  id: number;
  club_id: string;
  user_id: number;
  rank_id?: number;
  rank_date?: string;
  provincial_rank_id?: number;
  provincial_rank_date?: string;
  rate_id?: number;
  rate_date?: string;
  rate_point?: number;
  team_id?: number;
  is_team_leader: boolean;
  created_at: string;
  updated_at?: string;
}

export interface ErembeUserData extends User {
  rank_id?: number;
  rank_name?: string;
  rank_order?: number;
  rank_date?: string;
  provincial_rank_id?: number;
  provincial_rank_date?: string;
  rate_id?: number;
  rate_date?: string;
  rate_point?: number;
  team_id?: number;
  team_name?: string;
  is_team_leader: boolean;
}

export interface CreateUserClubRequest {
  club_id: string;
  user_id: number;
  rank_id?: number;
  rank_date?: string;
  provincial_rank_id?: number;
  provincial_rank_date?: string;
  rate_id?: number;
  rate_date?: string;
  rate_point?: number;
  team_id?: number;
  is_team_leader?: boolean;
}

export interface UpdateUserClubRequest extends Partial<CreateUserClubRequest> {}

export interface GetUserClubsParams extends PaginationParams {
  club_id?: string;
  user_id?: number;
  team_id?: number;
  is_team_leader?: boolean;
}

export interface ArcherUpsertRequest {
  ssn: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email?: string;
  gender?: string;
  affiliation?: string;
  club_id: string;
  team_id: number;
  rank_id?: number;
  rank_date?: string;
  provincial_rank_id?: number;
  provincial_rank_date?: string;
  rate_id?: number;
  rate_date?: string;
  is_team_leader?: boolean;
}

export interface ArcherUpsertResponse {
  success: boolean;
  user_id: number;
  is_new_user: boolean;
  is_new_membership: boolean;
  message: string;
}

export const userClubsApi = {
  getAll: (params?: GetUserClubsParams) =>
    apiClient.get<PaginatedResponse<UserClub>>('/user-clubs', { params }),

  getWithDetails: (clubId: string, params?: {
    user_id?: number;
    team_id?: number;
    role?: string;
    is_team_leader?: boolean;
    first_name?: string;
    last_name?: string;
    email?: string;
    ssn?: string;
    rank_id?: number;
    skip?: number;
    limit?: number;
  }) => {
    // If clubId is empty, don't include it in params (for superAdmin to see all)
    const queryParams = clubId ? { club_id: clubId, ...params } : params;
    return apiClient.get<PaginatedResponse<ErembeUserData>>('/user-clubs/with-details/', { params: queryParams });
  },

  getById: (id: number) =>
    apiClient.get<UserClub>(`/user-clubs/${id}`),

  create: (data: CreateUserClubRequest) =>
    apiClient.post<UserClub>('/user-clubs', data),

  update: (id: number, data: UpdateUserClubRequest) =>
    apiClient.put<UserClub>(`/user-clubs/${id}`, data),

  updateByClubAndUser: (clubId: string, userId: number, data: UpdateUserClubRequest) =>
    apiClient.put<UserClub>(`/user-clubs/${clubId}/users/${userId}`, data),

  delete: (id: number) =>
    apiClient.delete(`/user-clubs/${id}`),

  upsertArcher: (data: ArcherUpsertRequest) =>
    apiClient.post<ArcherUpsertResponse>('/user-clubs/upsert', data),
};

// ============================================================================
// EREMBE (Ordered Club Members)
// ============================================================================

export interface ErembeResponse {
  male?: ErembeUserData[];
  female?: ErembeUserData[];
  total_male?: number;
  total_female?: number;
  total?: number;
  items?: ErembeUserData[];
  gender?: string;
  skip?: number;
  limit?: number;
  page?: number;
  total_pages?: number;
}

export const erembeApi = {
  get: (clubId: string, params?: { gender?: 'male' | 'female'; skip?: number; limit?: number }) =>
    apiClient.get<ErembeResponse>('/erembe', { params: { club_id: clubId, ...params } }),
};

// ============================================================================
// AUDIT LOGS
// ============================================================================

export interface AuditLog {
  id: number;
  user_id: number;
  user_email: string;
  club_id: string;
  entity: string;
  entity_id: string;
  entity_name: string;
  action: 'create' | 'update' | 'delete';
  changes?: string;
  timestamp: string;
}

export interface GetAuditLogsParams extends PaginationParams {
  club_id?: string;
  entity?: string;
  action?: string;
  user_id?: number;
  start_date?: string;
  end_date?: string;
}

export const auditLogsApi = {
  getAll: (params?: GetAuditLogsParams) =>
    apiClient.get<PaginatedResponse<AuditLog>>('/audit-logs', { params }),

  getById: (id: number) =>
    apiClient.get<AuditLog>(`/audit-logs/${id}`),
};

// ============================================================================
// ZURKHAI (Competition Standings)
// ============================================================================

export interface Zurkhai {
  id: number;
  competition_id: number;
  user_id: number;
  user_first_name: string;
  user_last_name: string;
  team_id?: number;
  team_name?: string;
  club_id: string;
  club_name: string;
  rank_id?: number;
  rank_name?: string;
  stage_id?: number;
  order_id?: number;
  points?: number[];
  hana_points?: number[];
  has_points?: number[];
  created_at: string;
  updated_at?: string;
}

export interface CreateZurkhaiRequest {
  competition_id: number;
  user_id: number;
  user_first_name: string;
  user_last_name: string;
  team_id?: number;
  team_name?: string;
  club_id: string;
  club_name: string;
  rank_id?: number;
  rank_name?: string;
  stage_id?: number;
  order_id?: number;
  points?: number[];
  hana_points?: number[];
  has_points?: number[];
}

export interface UpdateZurkhaiRequest extends Partial<CreateZurkhaiRequest> {}

export interface GetZurkhaiParams extends PaginationParams {
  competition_id?: number;
  user_id?: number;
  club_id?: string;
}

export const zurkhaiApi = {
  getAll: (params?: GetZurkhaiParams) =>
    apiClient.get<PaginatedResponse<Zurkhai>>('/zurkhai', { params }),

  getById: (id: number) =>
    apiClient.get<Zurkhai>(`/zurkhai/${id}`),

  getByCompetitionAndUser: (competitionId: number, userId: number) =>
    apiClient.get<Zurkhai[]>('/zurkhai', { params: { competition_id: competitionId, user_id: userId, limit: 1 } }),

  create: (data: CreateZurkhaiRequest) =>
    apiClient.post<Zurkhai>('/zurkhai', data),

  update: (id: number, data: UpdateZurkhaiRequest) =>
    apiClient.put<Zurkhai>(`/zurkhai/${id}`, data),

  delete: (id: number) =>
    apiClient.delete(`/zurkhai/${id}`),
};

// ============================================================================
// COMPETITION REGISTRATIONS
// ============================================================================

export interface CompetitionRegistration {
  id: number;
  competition_id: number;
  user_id: number;
  user_name: string;
  invoice_id?: number;
  created_at: string;
  updated_at?: string;
}

export interface CreateCompetitionRegistrationRequest {
  competition_id: number;
  user_id: number;
  user_name: string;
  invoice_id?: number;
}

export interface UpdateCompetitionRegistrationRequest {
  competition_id?: number;
  user_id?: number;
  user_name?: string;
  invoice_id?: number;
}

export interface GetCompetitionRegistrationsParams extends PaginationParams {
  competition_id?: number;
  user_id?: number;
}

export const competitionRegistrationsApi = {
  getAll: (params?: GetCompetitionRegistrationsParams) =>
    apiClient.get<PaginatedResponse<CompetitionRegistration>>('/competition-registrations', { params }),

  getById: (id: number) =>
    apiClient.get<CompetitionRegistration>(`/competition-registrations/${id}`),

  create: (data: CreateCompetitionRegistrationRequest) =>
    apiClient.post<CompetitionRegistration>('/competition-registrations', data),

  update: (id: number, data: UpdateCompetitionRegistrationRequest) =>
    apiClient.put<CompetitionRegistration>(`/competition-registrations/${id}`, data),

  delete: (id: number) =>
    apiClient.delete(`/competition-registrations/${id}`),
};

// ============================================================================
// DASHBOARD
// ============================================================================

export interface DashboardStats {
  total_archers: number;
  club_archers: number;
  total_teams: number;
  total_referees: number;
  total_leaders: number;
  total_competitions: number;
  total_ranks: number;
}

export const dashboardApi = {
  getStats: (clubId: string) =>
    apiClient.get<DashboardStats>('/dashboard/stats', { params: { club_id: clubId } }),
};

// ============================================================================
// ADMINS (Admin Management - SuperAdmin only)
// ============================================================================

export interface Admin {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
}

export interface CreateAdminRequest {
  email: string;
  first_name: string;
  last_name: string;
  password?: string;
}

export interface GetAdminsParams extends PaginationParams {
  is_active?: boolean;
  search?: string;
}

export const adminsApi = {
  getAll: (params?: GetAdminsParams) =>
    apiClient.get<PaginatedResponse<Admin>>('/admins', { params }),

  getById: (id: number) =>
    apiClient.get<Admin>(`/admins/${id}`),

  create: (data: CreateAdminRequest) =>
    apiClient.post<Admin>('/admins', data),

  activate: (id: number) =>
    apiClient.put<Admin>(`/admins/${id}/activate`),

  inactivate: (id: number) =>
    apiClient.put<Admin>(`/admins/${id}/inactivate`),

  resetPassword: (id: number, newPassword: string) =>
    apiClient.put<Admin>(`/admins/${id}/reset-password`, { new_password: newPassword }),
};

// ============================================================================
// APP VERSIONS (Version Control - SuperAdmin only)
// ============================================================================

export interface AppVersion {
  id: number;
  platform: 'android' | 'ios';
  version: string;
  force_update: boolean;
  update_url?: string;
  created_at: string;
  updated_at?: string;
}

export interface CreateAppVersionRequest {
  platform: 'android' | 'ios';
  version: string;
  force_update: boolean;
  update_url?: string;
}

export interface UpdateAppVersionRequest {
  version?: string;
  force_update?: boolean;
  update_url?: string;
}

export const appVersionsApi = {
  getAll: () =>
    apiClient.get<AppVersion[]>('/app-versions'),

  getByPlatform: (platform: 'android' | 'ios') =>
    apiClient.get<AppVersion>(`/app-versions/${platform}`),

  create: (data: CreateAppVersionRequest) =>
    apiClient.post<AppVersion>('/app-versions', data),

  update: (platform: 'android' | 'ios', data: UpdateAppVersionRequest) =>
    apiClient.put<AppVersion>(`/app-versions/${platform}`, data),

  delete: (platform: 'android' | 'ios') =>
    apiClient.delete(`/app-versions/${platform}`),
};

// ============================================================================
// Export all
// ============================================================================

export default apiClient;
